import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTransactions } from '../contexts/TransactionContext';
import { useToast } from '../contexts/ToastContext';
import { useDialog } from '../contexts/DialogContext';
import TransactionSearch from './TransactionSearch';
import Icon from './Icon';
import { getCategoryIcon } from '../utils/categoryIcons';

const TransactionList = () => {
  const { transactions, updateTransaction, deleteTransaction, refreshTransactions } = useTransactions();
  const { showToast } = useToast();
  const { confirm } = useDialog();
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchResults, setSearchResults] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  
  // 선택된 월의 거래 필터링
  const monthTransactions = transactions.filter(t => {
    const tDate = new Date(t.date);
    const tMonth = `${tDate.getFullYear()}-${String(tDate.getMonth() + 1).padStart(2, '0')}`;
    return tMonth === selectedMonth;
  });
  
  // 날짜별로 그룹화
  const groupTransactionsByDate = (transactions) => {
    const grouped = {};
    transactions.forEach(transaction => {
      const date = new Date(transaction.date).toISOString().slice(0, 10);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(transaction);
    });
    
    // 날짜순으로 정렬 (최신순)
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
    const result = {};
    sortedDates.forEach(date => {
      result[date] = grouped[date].sort((a, b) => 
        new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
      );
    });
    
    return result;
  };
  const formatAmount = (amount, type) => {
    const formatted = Number(amount).toLocaleString('ko-KR');
    return type === 'income' ? `+${formatted}원` : `-${formatted}원`;
  };

  const handleEdit = (transaction) => {
    setEditingId(transaction.id);
    setEditForm({
      type: transaction.type,
      amount: parseInt(transaction.amount, 10),
      category: transaction.category,
      description: transaction.description || '',
      date: transaction.date
    });
  };

  const handleSave = async (id) => {
    try {
      await updateTransaction(id, {
        ...editForm,
        amount: parseInt(editForm.amount, 10),
      });
      setEditingId(null);
    } catch (error) {
      showToast({ type: 'error', message: '수정에 실패했습니다.' });
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: '거래 삭제',
      message: '정말 삭제하시겠습니까?',
      type: 'error',
      confirmText: '삭제',
    });
    if (!confirmed) return;
    
    try {
      await deleteTransaction(id);
    } catch (error) {
      showToast({ type: 'error', message: '삭제에 실패했습니다.' });
    }
  };

  const categories = {
    expense: ['식비', '주거비', '교통비', '통신비', '공과금', '쇼핑', '생활용품', '뷰티/미용', '여행/레저', '문화생활', '의료/약국', '교육/강의', '경조사비', '반려동물', '금융/투자', '기타'],
    income: ['급여', '부업', '투자수익', '용돈', '상여금', '기타']
  };

  const displayTransactions = searchResults || monthTransactions;
  const groupedTransactions = groupTransactionsByDate(displayTransactions);

  const monthLabel = new Date(selectedMonth + '-01').toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });

  return (
    <div>
      {/* 헤더 섹션 */}
      <div className="card">
        <div className={`flex-between ${showSearch ? 'transaction-header--expanded' : 'transaction-header'}`}>
          <div>
            <h2 className="transaction-header__title">
              <Icon name="receipt_long" size={28} /> 거래 내역
            </h2>
            <p className="transaction-header__subtitle">
              {searchResults ? `검색 결과 ${searchResults.length}건` : `${monthLabel} ${monthTransactions.length}건`}
            </p>
          </div>
          
          <div className="flex-center gap-8">
            <button
              className="btn-secondary btn-icon"
              onClick={() => {
                const date = new Date(selectedMonth + '-01');
                date.setMonth(date.getMonth() - 1);
                setSelectedMonth(date.toISOString().slice(0, 7));
                setSearchResults(null);
              }}
              aria-label="이전 달"
            >
              ‹
            </button>
            
            <div className="date-picker-wrapper">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDatePicker(!showDatePicker);
                  setPickerYear(new Date(selectedMonth).getFullYear());
                }}
              >
                <Icon name="calendar_today" size={16} /> {monthLabel}
              </button>

              {showDatePicker && (
                <div className="date-picker date-picker--dropdown">
                  <div className="flex-between mb-16">
                    <button
                      className="date-picker-item"
                      onClick={() => setPickerYear(pickerYear - 1)}
                    >
                      ‹
                    </button>
                    <h3 className="card-title">{pickerYear}년</h3>
                    <button
                      className="date-picker-item"
                      onClick={() => setPickerYear(pickerYear + 1)}
                    >
                      ›
                    </button>
                  </div>

                  <div className="date-picker-grid mb-16">
                    {Array.from({ length: 12 }, (_, monthIndex) => {
                      const monthValue = `${pickerYear}-${String(monthIndex + 1).padStart(2, '0')}`;
                      const isSelected = monthValue === selectedMonth;
                      const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

                      return (
                        <button
                          key={monthValue}
                          className={`date-picker-item${isSelected ? ' date-picker-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedMonth(monthValue);
                            setShowDatePicker(false);
                            setSearchResults(null);
                          }}
                        >
                          {monthNames[monthIndex]}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex-center date-picker__footer">
                    <button
                      className="date-picker-item"
                      onClick={() => {
                        const now = new Date();
                        setSelectedMonth(now.toISOString().slice(0, 7));
                        setPickerYear(now.getFullYear());
                        setShowDatePicker(false);
                        setSearchResults(null);
                      }}
                    >
                      이번 달
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="btn-secondary btn-icon"
              onClick={() => {
                const date = new Date(selectedMonth + '-01');
                date.setMonth(date.getMonth() + 1);
                setSelectedMonth(date.toISOString().slice(0, 7));
                setSearchResults(null);
              }}
              aria-label="다음 달"
            >
              ›
            </button>
            
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className={showSearch ? 'btn-danger' : 'btn-primary'}
              aria-label={showSearch ? '검색 닫기' : '검색 열기'}
            >
              <span>{showSearch ? <Icon name="close" size={16} /> : <Icon name="search" size={16} />}</span>
              {showSearch ? '닫기' : '검색'}
            </button>
          </div>
        </div>
        
        {showSearch && (
          <div className="transaction-search-area">
            <TransactionSearch 
              onResults={(results) => {
                setSearchResults(results);
                if (results === null) {
                  setShowSearch(false);
                }
              }} 
            />
          </div>
        )}
      </div>
      
      {/* 거래 리스트 */}
      <div className="card">
        {displayTransactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon-container">
              <Icon name="receipt_long" size={40} />
            </div>
            <h3 className="empty-state-title">
              {searchResults ? '검색 결과가 없습니다' : '거래 내역이 없습니다'}
            </h3>
            <p className="empty-state-description">
              {searchResults ? '다른 검색 조건을 시도해보세요' : '거래를 추가하면 여기에 표시됩니다'}
            </p>
          </div>
        ) : (
          <div className="transaction-group">
            {Object.entries(groupedTransactions).map(([date, dayTransactions]) => {
              const dateObj = new Date(date);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              
              let dateLabel;
              if (date === today.toISOString().slice(0, 10)) {
                dateLabel = '오늘';
              } else if (date === yesterday.toISOString().slice(0, 10)) {
                dateLabel = '어제';
              } else {
                dateLabel = dateObj.toLocaleDateString('ko-KR', { 
                  month: 'long', 
                  day: 'numeric',
                  weekday: 'short'
                });
              }
              
              const dayIncome = dayTransactions
                .filter(t => t.type === 'income')
                .reduce((sum, t) => sum + Number(t.amount), 0);
              const dayExpense = dayTransactions
                .filter(t => t.type === 'expense')
                .reduce((sum, t) => sum + Number(t.amount), 0);
              
              return (
                <div key={date}>
                  {/* 날짜 헤더 */}
                  <div className="transaction-date-header">
                    <h3 className="transaction-date-header__label">
                      {dateLabel}
                    </h3>
                    <div className="transaction-date-header__amounts">
                      {dayIncome > 0 && (
                        <span className="transaction-date-header__income">
                          +{dayIncome.toLocaleString('ko-KR')}원
                        </span>
                      )}
                      {dayExpense > 0 && (
                        <span className="transaction-date-header__expense">
                          -{dayExpense.toLocaleString('ko-KR')}원
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* 해당 날짜 거래 리스트 */}
                  <div className="transaction-day-list">
                    {dayTransactions.map(transaction => {
              
                      return (
                        <div 
                          key={transaction.id} 
                          className={editingId === transaction.id ? 'transaction-row transaction-row--editing' : 'transaction-row'}
                        >
                  {editingId === transaction.id ? (
                    // 수정 모드
                    <div className="transaction-edit-form">
                      <div className="transaction-edit-grid">
                        <div className="form-group">
                          <label className="form-label">유형</label>
                          <select 
                            value={editForm.type} 
                            onChange={(e) => setEditForm({...editForm, type: e.target.value, category: ''})}
                            className="form-select"
                          >
                            <option value="expense">지출</option>
                            <option value="income">수입</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">금액</label>
                          <input 
                            type="number" 
                            step="1"
                            value={editForm.amount} 
                            onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                            placeholder="금액을 입력하세요"
                            className="form-input"
                          />
                        </div>
                      </div>
                      <div className="transaction-edit-grid">
                        <div className="form-group">
                          <label className="form-label">카테고리</label>
                          <select 
                            value={editForm.category} 
                            onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                            className="form-select"
                          >
                            <option value="">카테고리 선택</option>
                            {categories[editForm.type]?.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">날짜</label>
                          <input 
                            type="date" 
                            value={editForm.date} 
                            onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                            className="form-input"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">설명</label>
                        <input 
                          type="text" 
                          value={editForm.description} 
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                          placeholder="거래 설명을 입력하세요"
                          className="form-input"
                        />
                      </div>
                      <div className="transaction-edit-actions">
                        <button 
                          onClick={() => handleSave(transaction.id)}
                          className="btn-primary"
                        >
                          저장
                        </button>
                        <button 
                          onClick={() => setEditingId(null)}
                          className="btn-secondary"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    // 일반 모드
                    <div className="transaction-row__content">
                      <div className="transaction-row__icon">
                        <Icon name={getCategoryIcon(transaction.category)} size={18} />
                      </div>
                      <div className="transaction-row__info">
                        <div className="transaction-row__title">
                          {transaction.description || transaction.category}
                        </div>
                        <div className="transaction-row__meta">
                          <span>{transaction.category}</span>
                          <span>•</span>
                          <span>{new Date(transaction.created_at || transaction.date).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                      
                      <div className="transaction-row__actions">
                        <div className={`transaction-row__amount ${transaction.type === 'income' ? 'transaction-row__amount--income' : ''}`}>
                          {formatAmount(transaction.amount, transaction.type)}
                        </div>
                        
                        <div className="transaction-row__buttons">
                          <button 
                            onClick={() => handleEdit(transaction)}
                            className="btn-primary btn-sm"
                            aria-label={`${transaction.description || transaction.category} 수정`}
                          >
                            수정
                          </button>
                          <button 
                            onClick={() => handleDelete(transaction.id)}
                            className="btn-danger btn-sm"
                            aria-label={`${transaction.description || transaction.category} 삭제`}
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

TransactionList.propTypes = {};

export default TransactionList;
